import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Loader2, MapPin, ArrowRight, Star, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import FeedCard from "../components/FeedCard";
import SafeImage from "../components/SafeImage";
import AiPhotoBadge from "../components/AiPhotoBadge";
import BannerAd from "../components/BannerAd";
import BoostedRail from "../components/BoostedRail";
import SEO from "../components/SEO";
import VerticalShortcuts from "../components/VerticalShortcuts";
import { canonicalForCategory } from "../lib/seo";
import { logInternalSearch } from "../lib/analytics";
import { planBannerSlots } from "../lib/bannerSlots";

const SEO_BY_CAT = {
  all: ["Feed de João Pessoa", "Tudo o que acontece em João Pessoa agora: eventos, promoções, gastronomia e novidades da cidade."],
  "onde-comer": ["Onde comer em João Pessoa", "Os melhores restaurantes, bares e cafés de João Pessoa/PB recomendados pela IA."],
  eventos: ["Eventos em João Pessoa", "Shows, festivais e eventos em João Pessoa/PB. Veja o que está rolando na cidade."],
  promocoes: ["Promoções em João Pessoa", "Descontos e ofertas de restaurantes, lojas e serviços em João Pessoa/PB."],
  empresas: ["Empresas em João Pessoa", "Encontre empresas e serviços em João Pessoa/PB."],
  hoteis: ["Hotéis em João Pessoa", "Hotéis e pousadas à beira-mar em João Pessoa/PB. Reserve sua estadia."],
  praias: ["Praias de João Pessoa", "Guia das praias de João Pessoa/PB: Tambaú, Cabo Branco, Bessa e mais."],
  imoveis: ["Imóveis em João Pessoa", "Apartamentos, casas e lançamentos em João Pessoa/PB."],
  empregos: ["Empregos em João Pessoa", "Vagas e oportunidades de emprego em João Pessoa/PB."],
  saude: ["Saúde em João Pessoa", "Clínicas, hospitais e serviços de saúde em João Pessoa/PB."],
  noticias: ["Notícias de João Pessoa", "As últimas notícias de João Pessoa e Paraíba, explicadas pela IA."],
  turismo: ["Turismo em João Pessoa", "Passeios, pontos turísticos e roteiros em João Pessoa/PB."],
};

const CATS = [
  { key: "all", label: "Tudo" },
  { key: "onde-comer", label: "Onde comer" },
  { key: "noticias", label: "Notícias" },
  { key: "eventos", label: "Eventos" },
  { key: "bares", label: "Bares" },
  { key: "hoteis", label: "Hotéis" },
  { key: "praias", label: "Praias" },
  { key: "saude", label: "Saúde" },
  { key: "promocoes", label: "Promoções" },
  { key: "empresas", label: "Empresas" },
  { key: "turismo", label: "Turismo" },
  { key: "imoveis", label: "Imóveis" },
  { key: "empregos", label: "Empregos" },
];

// Chips that represent real businesses -> open the Empresas listing (not news)
const BIZ_CATS = { "onde-comer": "onde-comer", bares: "empresas", hoteis: "hoteis", saude: "saude", empresas: "empresas" };

export default function Feed() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const category = params.get("category") || "all";
  const initialQ = params.get("q") || "";

  const [q, setQ] = useState(initialQ);
  const [items, setItems] = useState([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const loaderRef = useRef(null);
  const LIMIT = 5;
  // A fresh seed per mount / per category-or-query change: keeps infinite scroll
  // consistent across pages of the same session while shuffling the order on
  // every visit so the feed never feels like the same sequence twice.
  const [feedSeed, setFeedSeed] = useState(() => Math.floor(Math.random() * 2_000_000_000));

  const [newBiz, setNewBiz] = useState([]);
  useEffect(() => {
    api.get("/businesses", { params: { sort: "recent", limit: 12, seed: feedSeed } }).then(({ data }) => setNewBiz(data.items || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedSeed]);
  const showNewBiz = category === "all" && !initialQ && newBiz.length > 0;

  const [banners, setBanners] = useState([]);
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api.get("/banners", { params: { placement: "feed" } }).then(({ data }) => setBanners(data.items || [])).catch(() => {});
    api.get("/businesses", { params: { featured: 1, limit: 12 } }).then(({ data }) => setFeatured(data.items || [])).catch(() => {});
  }, []);
  const showFeatured = category === "all" && !initialQ && featured.length > 0;

  const fetchPage = useCallback(async (reset = false) => {
    if (loading) return;
    setLoading(true);
    const currentSkip = reset ? 0 : skip;
    try {
      const { data } = await api.get("/feed", { params: { category, q: initialQ || undefined, skip: currentSkip, limit: LIMIT, seed: feedSeed } });
      setItems((prev) => {
        if (reset) return data.items;
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...data.items.filter((n) => !seen.has(n.id))];
      });
      setSkip(currentSkip + data.items.length);
      setHasMore(data.has_more);
    } catch { setHasMore(false); }
    finally { setLoading(false); }
  }, [category, initialQ, skip, loading]);

  useEffect(() => {
    setItems([]); setSkip(0); setHasMore(true);
    // Gera um novo seed a cada troca de categoria/busca — o feed é
    // reordenado (mantendo 2 RSS + 1 AI e datas mais recentes primeiro),
    // então nunca mostra a mesma sequência ao voltar para o mesmo filtro.
    setFeedSeed(Math.floor(Math.random() * 2_000_000_000));
    fetchPage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, initialQ]);

  useEffect(() => {
    const el = loaderRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading) fetchPage(false);
    }, { rootMargin: "300px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading, fetchPage]);

  const submitSearch = (e) => {
    e.preventDefault();
    const term = q.trim();
    if (!term) { const next = new URLSearchParams(params); next.delete("q"); setParams(next); return; }
    logInternalSearch({ query: term, source: "feed", category: category === "all" ? null : category });
    navigate(`/busca?q=${encodeURIComponent(term)}`);
  };

  const setCat = (key) => {
    if (BIZ_CATS[key]) { navigate(`/empresas?category=${BIZ_CATS[key]}`); return; }
    // Clicando na mesma categoria ativa → força refresh embaralhado.
    if (key === category) {
      setItems([]); setSkip(0); setHasMore(true);
      setFeedSeed(Math.floor(Math.random() * 2_000_000_000));
      fetchPage(true);
      return;
    }
    const next = new URLSearchParams();
    if (key !== "all") next.set("category", key);
    if (initialQ) next.set("q", initialQ);
    setParams(next);
  };

  const refreshFeed = () => {
    setItems([]); setSkip(0); setHasMore(true);
    setFeedSeed(Math.floor(Math.random() * 2_000_000_000));
    fetchPage(true);
  };

  const [seoTitle, seoDesc] = SEO_BY_CAT[category] || SEO_BY_CAT.all;
  const bannerSlots = React.useMemo(() => planBannerSlots(items.length, banners, feedSeed), [items.length, banners, feedSeed]);

  return (
    <div className="space-y-5">
      <SEO title={initialQ ? `Busca: ${initialQ}` : seoTitle} description={seoDesc}
        path={category !== "all" ? `/feed?category=${category}` : "/feed"}
        canonicalPath={initialQ ? "/feed" : canonicalForCategory(category, "/feed")}
        noindex={!!initialQ} />
      <div className="sticky top-[152px] md:top-0 z-20 bg-background/95 backdrop-blur-md -mx-4 px-4 pt-1 pb-3 md:mx-0 md:px-0">
        <form onSubmit={submitSearch} className="relative mb-3">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            data-testid="feed-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar na cidade..."
            className="w-full h-12 pl-12 pr-4 rounded-full bg-card border border-border text-base focus:ring-2 focus:ring-primary outline-none"
          />
        </form>
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1 md:flex-wrap md:overflow-visible md:pb-0 items-center">
          <button
            data-testid="feed-refresh"
            onClick={refreshFeed}
            title="Atualizar feed"
            className="shrink-0 w-9 h-9 rounded-full bg-secondary text-secondary-foreground hover:bg-muted flex items-center justify-center active:scale-95 active:rotate-180 transition-transform"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          {CATS.map((c) => (
            <button
              key={c.key}
              data-testid={`feed-cat-${c.key}`}
              onClick={() => setCat(c.key)}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors active:scale-95 ${
                category === c.key ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground hover:bg-muted"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <VerticalShortcuts />

      {category === "all" && !initialQ && <BoostedRail data-testid="feed-boosted-rail" />}

      {showNewBiz && (
        <section data-testid="home-new-businesses">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-heading font-bold text-lg">Novos no Guia ✨</h2>
              <p className="text-sm text-muted-foreground">Estabelecimentos reais recém-adicionados</p>
            </div>
            <button data-testid="home-see-businesses" onClick={() => navigate("/empresas")} className="text-sm text-primary font-semibold flex items-center gap-1">
              Ver todos <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 -mx-4 px-4 md:mx-0 md:px-0">
            {newBiz.map((b, i) => (
              <motion.button
                key={b.id}
                data-testid={`home-new-biz-${b.id}`}
                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: Math.min(i * 0.05, 0.4) }}
                onClick={() => navigate(`/empresa/${b.slug}`)}
                className="w-52 shrink-0 text-left rounded-3xl overflow-hidden border border-border bg-card soft-shadow hover:-translate-y-1 transition-transform"
              >
                <div className="relative h-28">
                  {b.cover ? (
                    <SafeImage src={b.cover} category={b.category} alt={b.name} loading="lazy" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary flex items-center justify-center">
                      <span className="font-heading font-extrabold text-2xl text-primary">{b.name?.[0]?.toUpperCase()}</span>
                    </div>
                  )}
                  {b.instagram && (
                    <span className="absolute top-2 left-2 rounded-full bg-black/55 backdrop-blur-sm text-white text-[10px] font-semibold px-2 py-0.5">Nas redes</span>
                  )}
                  {b.cover && b.ai_image && <AiPhotoBadge />}
                </div>
                <div className="p-3">
                  <p className="font-heading font-bold text-sm leading-tight line-clamp-1">{b.name}</p>
                  <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="w-3 h-3 shrink-0" /><span className="line-clamp-1">{b.neighborhood || b.address}</span></p>
                </div>
              </motion.button>
            ))}
          </div>
        </section>
      )}

      {showFeatured && (
        <section data-testid="feed-featured">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-heading font-bold text-lg flex items-center gap-2"><Star className="w-5 h-5 fill-alert text-alert" /> Destaques</h2>
              <p className="text-sm text-muted-foreground">Empresas em destaque no Guia</p>
            </div>
          </div>
          <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 -mx-4 px-4 md:mx-0 md:px-0">
            {featured.map((b, i) => (
              <motion.button
                key={b.id}
                data-testid={`feed-featured-${b.id}`}
                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: Math.min(i * 0.05, 0.4) }}
                onClick={() => navigate(`/empresa/${b.slug}`)}
                className="w-56 shrink-0 text-left rounded-3xl overflow-hidden border-2 border-primary/40 bg-card soft-shadow hover:-translate-y-1 transition-transform"
              >
                <div className="relative h-28">
                  {b.cover ? (
                    <SafeImage src={b.cover} category={b.category} alt={b.name} loading="lazy" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary flex items-center justify-center">
                      <span className="font-heading font-extrabold text-2xl text-primary">{b.name?.[0]?.toUpperCase()}</span>
                    </div>
                  )}
                  <span className="absolute top-2 left-2 flex items-center gap-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5"><Star className="w-3 h-3 fill-current" /> Destaque</span>
                  {b.cover && b.ai_image && <AiPhotoBadge />}
                </div>
                <div className="p-3">
                  <p className="font-heading font-bold text-sm leading-tight line-clamp-1">{b.name}</p>
                  <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="w-3 h-3 shrink-0" /><span className="line-clamp-1">{b.neighborhood || b.address}</span></p>
                </div>
              </motion.button>
            ))}
          </div>
        </section>
      )}

      <div className="grid gap-6">
        {items.map((p, i) => (
          <React.Fragment key={p.id}>
            <FeedCard post={p} index={i} onAskAI={(post) => navigate(`/ia?p=${encodeURIComponent(post.title)}`)} />
            {bannerSlots[i] && <BannerAd key={`${bannerSlots[i].id}-${i}`} banner={bannerSlots[i]} />}
          </React.Fragment>
        ))}
      </div>

      {items.length === 0 && !loading && (
        <div data-testid="feed-empty" className="text-center py-16 text-muted-foreground">
          Nenhum resultado encontrado. Tente outra categoria ou busca.
        </div>
      )}

      <div ref={loaderRef} className="flex justify-center py-8">
        {loading && <Loader2 className="w-6 h-6 animate-spin text-primary" data-testid="feed-loading" />}
        {!hasMore && items.length > 0 && <p className="text-sm text-muted-foreground">Você chegou ao fim ✨</p>}
      </div>
    </div>
  );
}
